import { useRef } from 'react';
import useNonOwnedSequenceActionGuard from './useNonOwnedSequenceActionGuard';

export type ConfirmEdit = (applyEdit: () => void) => void;

/**
 * Confirms the first edit a manager makes to a sequence owned by someone else.
 *
 * Personalizing is made of many small edits — nudging a delay, toggling
 * automatic, retyping a subject — so asking every time would be unusable. The
 * prompt appears once per opened sequence: the ref is keyed by sequence
 * instance, so opening a different one asks again, and nothing is remembered
 * across a remount.
 */
const useOnBehalfEditConfirm = (
  sequenceInstanceId: number | undefined,
  onBehalf: boolean,
): { confirmEdit: ConfirmEdit } => {
  const { openNonOwnedSequenceActionModal } = useNonOwnedSequenceActionGuard();
  const confirmedForRef = useRef<number | null>(null);

  const confirmEdit: ConfirmEdit = (applyEdit) => {
    const alreadyConfirmed =
      sequenceInstanceId !== undefined &&
      confirmedForRef.current === sequenceInstanceId;

    if (!onBehalf || alreadyConfirmed) {
      applyEdit();
      return;
    }

    openNonOwnedSequenceActionModal('edit', () => {
      confirmedForRef.current = sequenceInstanceId ?? null;
      applyEdit();
    });
  };

  return { confirmEdit };
};

export default useOnBehalfEditConfirm;
